import React, { useState, useEffect } from 'react';
import DriverHeader from '../components/common/Driver'; 
import DriverTable from '../components/desktop/Driver';
import DriverCard from '../components/mobile/Driver';
import DriverAttendancePage from './DriverAttendance';
import DriverExpenses from './DriverExpense';

import DriverForm from '../components/form/Driver';
import Pagination from '../components/Pagination';
import Spinner from '../components/ui/Spinner';
import { getBranches } from '../services/branches';
import { getDrivers, toggleDriverStatus } from '../services/drivers';
import { USER_ROLE } from '../services/auth';
import ConfirmationModal from '../components/modals/ConfirmationModal';

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [count, setCount] = useState(0);
  const [branches, setBranches] = useState([]);
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverType, setDriverType] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showExpenses, setShowExpenses] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [expenseDriver, setExpenseDriver] = useState(null);
  const [attendanceDriver, setAttendanceDriver] = useState(null);
  const [inputSearch, setInputSearch] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [branchFilter, setBranchFilter] = useState(0);
  const [driverTypeFilter, setDriverTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
  });

  const driversPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [search, statusFilter, branchFilter, driverTypeFilter]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchDrivers(pendingPage);
    }
    const user = localStorage.getItem(USER_ROLE);
    if (user == 'admin' && branches.length == 0) {
      fetchBranches();
    }
  }, [pendingPage]);

  const handleAdd = (driver_type) => {
    setEditingDriver(null);
    setDriverType(driver_type);
    setShowForm(true);
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setDriverType(driver.driver_type);
    setShowForm(true);
  };

  const fetchBranches = async () => {
    setLoading(true);
    await getBranches({ paginate: false })
      .then(([err, data]) => {
        setBranches(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const fetchDrivers = async (page) => {
    setLoading(true);
    await getDrivers({
      page,
      search,
      status: statusFilter.toLowerCase(),
      branch: Number(branchFilter) || 0,
      driver_type: driverTypeFilter,
      pageSize: driversPerPage
    })
      .then(([err, data]) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        const normalizedFilter = (statusFilter || 'all').toLowerCase();
        const filteredRows = normalizedFilter === 'active'
          ? rows.filter((item) => Boolean(item?.is_active))
          : normalizedFilter === 'inactive'
          ? rows.filter((item) => !Boolean(item?.is_active))
          : rows;
        setDrivers(filteredRows);
        setCount(data.count);
        setCurrentPage(page);
        setPendingPage(null);
        setLoading(false);
      })
      .catch((error) => {
        setPendingPage(null);
        setLoading(false);
      });
  };

  const handleSave = async (response) => {
    await fetchDrivers(currentPage);
    setShowForm(false);
  };

  const handleToggleStatus = async (driver) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: driver.is_active ? 'Deactivate Driver' : 'Activate Driver',
      message: `Are you sure you want to ${driver.is_active ? 'deactivate' : 'activate'} the driver "${driver.name}"?`,
      onConfirm: () => handleAction(driver),
    });
  };

  const handleAction = async (driver) => {
    setLoading(true);
    await toggleDriverStatus(driver.id, !driver.is_active);
    setConfirmModalParams({...confirmModalParams, open: false});
    await fetchDrivers(currentPage);
    setLoading(false);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setSearch(inputSearch.trim());
    setPendingPage(1);
  };

  const onTextChange = (e) => {
    setInputSearch(e.target.value);
  };

  const onDownload = async (driver, type) => {
    const fileUrl = driver[`${type}`];
    const filename = fileUrl.split('/').pop();
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleViewExpenses = (driver) => {
    setExpenseDriver(driver);
    setShowExpenses(true);
  };

  const handleViewAttendance = (driver) => {
    setAttendanceDriver(driver);
    setShowAttendance(true);
  };

  const onExpenseBack = () => {
    setExpenseDriver(null);
    setShowExpenses(false);
  };

  const onAttendanceBack = () => {
    setAttendanceDriver(null);
    setShowAttendance(false);
  };

  const onClearSearch = () => {
    setInputSearch('');
    setSearch('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const onBranchFilter = (e) => {
    setBranchFilter(Number(e.target.value) || 0);
    setCurrentPage(1);
    setPendingPage(1);
  };

  const onDriverTypeFilter = (e) => {
    setDriverTypeFilter(e.target.value || '');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const onStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
    setPendingPage(1);
  };

  const handleDriverSelection = (driver) => {
    setEditingDriver(driver);
    setDriverType(driver.driver_type);
    setShowForm(true);
  };

  const handleCancel = async () => {
    setShowForm(false);
  };

  return (
    <div className="p-4 w-full">
      {!showAttendance && (
        <DriverHeader
          inputSearch={inputSearch}
          count={count}
          onSearch={onSearch}
          onTextChange={onTextChange}
          handleAdd={handleAdd}
          onClearSearch={onClearSearch}
          onBack={handleCancel}
          driver={editingDriver}
          formName={showForm ? "add_edit" : (showExpenses ? "expense" : null)}
          showBranches={localStorage.getItem(USER_ROLE) === 'admin'}
          branches={branches}
          branchFilter={branchFilter}
          onBranchFilter={onBranchFilter}
          driverTypeFilter={driverTypeFilter}
          onDriverTypeFilter={onDriverTypeFilter}
          statusFilter={statusFilter}
          onStatusFilter={onStatusFilter}
        />
      )}

      {showForm ? (
        <DriverForm
          driver={editingDriver}
          driverType={driverType}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : ((showExpenses ? (
        <DriverExpenses
          driverList={drivers}
          driver={expenseDriver}
          onBack={onExpenseBack}
        />
      ) : (
        showAttendance ? (
          <DriverAttendancePage
          driver={attendanceDriver}
          onBack={onAttendanceBack}
        />
        ) : (
          <>
            {loading ? <Spinner /> : (
              <>
                <DriverCard
                  drivers={drivers}
                  handleEdit={handleEdit}
                  onDownload={onDownload}
                  onToggleStatus={handleToggleStatus}
                />

                <DriverTable
                  drivers={drivers}
                  handleEdit={handleEdit}
                  onDownload={onDownload}
                  onToggleStatus={handleToggleStatus}
                  onViewExpenses={handleViewExpenses}
                  onViewAttendance={handleViewAttendance}
                  onDriverSelected={handleDriverSelection}
                />
              </>
            )}
            {/* ✅ Pagination only if users exist */}
            <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
              {drivers.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.max(1, Math.ceil(count / driversPerPage))}
                  onPageChange={setPendingPage}
                />
              )}
            </div>
          </>
        )
      )))}
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams({...confirmModalParams, open: false})}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

export default Drivers;
